import http from '@/api/http'

export interface UploadedFile {
    id: number
    documentId: string
    name: string
    alternativeText: string | null
    caption: string | null
    width: number | null
    height: number | null
    formats: Record<string, unknown> | null
    hash: string
    ext: string
    mime: string
    size: number
    url: string
    previewUrl: string | null
    provider: string
    provider_metadata: unknown | null
    createdAt: string
    updatedAt: string
    publishedAt: string
}

/**
 * Upload a file to Strapi
 * @param file - File to upload
 * @param options - options with the following sub attributes:
 *  "ref" - Optional reference to content type (e.g., 'api::company.company')
 *  "refId" - Optional reference ID (documentId of the entity)
 *  "field" - Optional field name (e.g., 'logo')
 * @returns Promise resolving to uploaded file metadata with ID
 */
export async function uploadFile(
    file: File,
    options?: {
        ref?: string
        refId?: string
        field?: string
        onUploadProgress?: (percent: number) => void
    }
): Promise<UploadedFile> {
    const formData = new FormData()
    formData.append('files', file)

    if (options?.ref) formData.append('ref', options.ref)
    if (options?.refId) formData.append('refId', options.refId)
    if (options?.field) formData.append('field', options.field)

    const response = await http.post<UploadedFile[]>('/upload', formData, {
        // we need to override the "Content-Type" here because the http.ts sets the "Content-Type" to
        // 'application/json' as default value. This override is necessary to let the browser choose
        // the correct "Content-Type" here depending on the upload files.
        headers: {
            'Content-Type': undefined,
        },
        onUploadProgress: (event) => {
            if (!options?.onUploadProgress || !event.total) return
            options.onUploadProgress(Math.round((event.loaded / event.total) * 100))
        },
    })

    // Strapi returns array of uploaded files, we only upload one at a time
    if (!response.data || response.data.length === 0) {
        throw new Error('Upload failed: no file returned from server')
    }
    return response.data[0]
}

/**
 * Delete a file from Strapi
 * @param fileId - File ID to delete
 */
export async function deleteFile(fileId: number): Promise<void> {
    await http.delete(`/upload/files/${fileId}`)
}
